// src/pages/Trainings.jsx
// Archivio delle sessioni di allenamento salvate (area operatore).
// Ogni sessione si apre via /training/:id (replay e analisi, senza password).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import './Trainings.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); }
  catch { return iso; }
};
const fmtDur = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

export default function Trainings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null); // sessione in attesa di conferma eliminazione

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/training/recordings`)
      .then(r => r.json())
      .then(j => setItems(j?.trainings || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE}/api/training/recordings/${id}`, { method: 'DELETE' });
      load();
    } catch { /* noop */ }
    setConfirmId(null);
  };

  return (
    <div className="rs-page">
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--lime"><span className="rs-kicker__dot rs-kicker__dot--lime" />Archivio</span>
          <h1 className="rs-page-header__title">Allenamenti passati</h1>
          <p className="rs-page-header__subtitle">
            Sessioni salvate. Apri una sessione per il replay e l'analisi, o condividine il link.
          </p>
        </div>
        <div className="rs-page-header__actions">
          <span className="rs-chip"><Icon.Archive /><strong>{items.length}</strong> {items.length === 1 ? 'sessione' : 'sessioni'}</span>
        </div>
      </header>

      {loading ? (
        <div className="tr-loading"><span className="rs-spinner" />Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="rs-empty">
          <Icon.Archive />
          <strong>Nessuna sessione salvata</strong>
          <p>Le sessioni di allenamento terminate compaiono qui, pronte per il replay.</p>
        </div>
      ) : (
        <div className="rs-table-wrap">
          <table className="rs-table rs-table--dense tr-table">
            <thead>
              <tr>
                <th>Circuito</th>
                <th>Data</th>
                <th className="num">Piloti</th>
                <th className="num">Durata</th>
                <th>Giri per pilota</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {items.map(t => {
                const drivers = Array.isArray(t.drivers) ? t.drivers : [];
                return (
                  <React.Fragment key={t.id}>
                    <tr className="is-clickable" onClick={() => navigate(`/training/${t.id}`)} title="Apri replay e analisi">
                      <td><span className="tr-circuit">{t.circuitName}</span></td>
                      <td><span className="tr-date">{fmtDate(t.startTime)}</span></td>
                      <td className="num">{t.driversCount}</td>
                      <td className="num">{fmtDur(t.durationSeconds)}</td>
                      <td>
                        {drivers.length > 0 ? (
                          <div className="tr-chips">
                            {drivers.slice(0, 6).map(d => (
                              <span key={d.mac} className="rs-chip" title={d.car ? `${d.car}: ${(d.pilots || []).join(', ')}` : d.fullName}>
                                <strong>{d.tag || d.fullName}</strong>
                                {Array.isArray(d.pilots) && d.pilots.length > 0 && <span className="tr-pilots">{d.pilots.join(', ')}</span>}
                                {d.lapCount} giri
                              </span>
                            ))}
                            {drivers.length > 6 && <span className="tr-more">+{drivers.length - 6}</span>}
                          </div>
                        ) : <span className="rs-text-faint">—</span>}
                      </td>
                      <td>
                        <div className="tr-actions">
                          <button type="button" className="rs-btn rs-btn--sm rs-btn--outline" onClick={(e) => { e.stopPropagation(); navigate(`/training/${t.id}`); }}>
                            Apri<Icon.ArrowRight />
                          </button>
                          <button type="button" className="rs-btn rs-btn--sm rs-btn--icon rs-btn--ghost" title="Elimina sessione" aria-label="Elimina sessione"
                            onClick={(e) => { e.stopPropagation(); setConfirmId(confirmId === t.id ? null : t.id); }}>
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {confirmId === t.id && (
                      <tr className="tr-confirm-row" onClick={e => e.stopPropagation()}>
                        <td colSpan={6}>
                          <div className="rs-confirm">
                            <div className="rs-confirm__text">
                              Eliminare definitivamente la sessione <strong>{t.circuitName}</strong> del {fmtDate(t.startTime)}? L'operazione non è reversibile.
                            </div>
                            <div className="rs-confirm__actions">
                              <button type="button" className="rs-btn rs-btn--sm" onClick={(e) => { e.stopPropagation(); setConfirmId(null); }}>Annulla</button>
                              <button type="button" className="rs-btn rs-btn--sm rs-btn--danger" onClick={(e) => handleDelete(t.id, e)}><TrashIcon />Elimina</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
